<template>
  <div class="app-dashboard">
    <div class="dashboard-container">
      <div class="row row-cols-1 dashboard-row">
        <div class="col dashboard-col">
          <chart-card
            :title="t('Top Active Providers')"
            :loading="loading"
            :is-empty="!hasTopActiveProvidersData"
            :empty-text="t('No provider data available')"
          >
            <div class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>{{ t('Provider') }}</th>
                    <th>{{ t('Consignment Count') }}</th>
                    <th>{{ t('Total Price') }}</th>
                    <th>{{ t('Total Profit') }}</th>
                    <th>{{ t('Profit Margin') }}</th>
                  </tr>
                </thead>
                <tbody v-if="!loading && hasTopActiveProvidersData">
                  <tr v-for="(item, index) in getTopActiveProvidersItems" :key="index">
                    <td>{{ item.name }}</td>
                    <td>{{ item.consignmentCount }}</td>
                    <td>{{ item.totalPrice }}</td>
                    <td>{{ item.totalProfit }}</td>
                    <td>{{ item.profitMargin }}</td>
                  </tr>
                </tbody>
                <tbody v-else-if="loading">
                  <tr>
                    <td colspan="5" class="text-center">{{ t('Loading...') }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="5" class="text-center">{{ t('No data available') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </chart-card>
        </div>
      </div>

      <!--
      <div class="row row-cols-1 row-cols-md-2 dashboard-row">
        <div class="col dashboard-col">
          <chart-card
            :title="t('Consignment Count by Provider')"
            :loading="loading"
            :is-empty="!hasTopActiveProvidersData"
            :empty-text="t('No provider data available')"
          >
            <pie-chart
              :data="getConsignmentCountPieData"
              :loading="loading"
              :is-empty="!hasTopActiveProvidersData"
              :doughnut="true"
            />
          </chart-card>
        </div>

        <div class="col dashboard-col">
          <chart-card
            :title="t('Total Profit by Provider')"
            :loading="loading"
            :is-empty="!hasTopActiveProvidersData"
            :empty-text="t('No provider data available')"
          >
            <bar-chart
              :categories="getProviderNames"
              :series="getTotalProfitSeries"
              :loading="loading"
              :is-empty="!hasTopActiveProvidersData"
              :colors="['#28C76F']"
              :height="300"
              :value-formatter="formatCurrency"
            />
          </chart-card>
        </div>
      </div>
      -->

      <div class="row row-cols-1 dashboard-row">
        <div class="col dashboard-col">
          <chart-card
            :title="t('Delivery Time Analysis')"
            :loading="loading"
            :is-empty="!hasProviderStateDeliveryTimesData"
            :empty-text="t('No delivery time data available')"
          >
            <bar-chart
              :categories="getDeliveryTimeCategories"
              :series="getAverageDeliveryTimeSeries"
              :loading="loading"
              :is-empty="!hasProviderStateDeliveryTimesData"
              :colors="['#00CFE8']"
              :value-formatter="formatNumber"
              @bar-click="onDeliveryTimeBarClick"
            />
          </chart-card>
        </div>
      </div>

      <!-- Provider details modal -->
      <transition name="pp-backdrop-fade">
        <div
          v-if="showProviderDetails"
          class="pp-modal-backdrop"
          @click.self="closeProviderDetails"
        >
          <transition name="pp-modal-zoom">
            <div class="pp-modal" :class="{ 'is-dark': isDark }">
              <div class="pp-modal-header">
                <h5 class="mb-0">{{ selectedProviderName }}</h5>
                <button
                  type="button"
                  class="close"
                  aria-label="Close"
                  @click="closeProviderDetails"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div class="pp-modal-body">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>{{ stateHeaderLabel }}</th>
                        <th>{{ cityHeaderLabel }}</th>
                        <th>{{ t('Average Delivery Days') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, idx) in providerDetailsRows" :key="idx">
                        <td>{{ row.state || '-' }}</td>
                        <td>{{ row.city || '-' }}</td>
                        <td>{{ formatNumber(row.avg) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import useService from '@/auth/service/useService';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import useAppConfig from '@core/app-config/useAppConfig';
import { computed } from '@vue/composition-api';
import { ChartCard, BarChart } from '../../components';

export default {
  components: {
    ChartCard,
    BarChart
  },
  mixins: [helper],
  props: {
    selectedFilter: {
      type: String,
      default: 'thisYear'
    },
    shipmentDirection: {
      type: [Number, String],
      default: null
    }
  },
  setup() {
    const { t } = useI18nUtils();
    const { skin } = useAppConfig();
    const isDark = computed(() => skin.value === 'dark');
    return {
      t,
      isDark
    };
  },
  data() {
    return {
      loading: true,
      dashboardData: {},
      showProviderDetails: false,
      selectedProviderName: ''
    };
  },
  computed: {
    fetchKey() {
      return [this.selectedFilter, this.shipmentDirection].join('|');
    },
    hasTopActiveProvidersData() {
      return (
        this.dashboardData.top10_active_providers &&
        this.dashboardData.top10_active_providers.length > 0
      );
    },
    hasProviderStateDeliveryTimesData() {
      return (
        this.dashboardData.provider_state_delivery_times &&
        this.dashboardData.provider_state_delivery_times.length > 0
      );
    },
    aggregatedAverageDeliveryByProvider() {
      if (!this.hasProviderStateDeliveryTimesData) return { categories: [], averages: [] };

      const aggregationMap = new Map();
      this.dashboardData.provider_state_delivery_times.forEach(item => {
        const name = item.provider_name || String(item.provider_id || 'Unknown');
        const value = Number(item.avg_delivery_day);
        if (!Number.isFinite(value)) return;
        if (!aggregationMap.has(name)) {
          aggregationMap.set(name, { sum: 0, count: 0 });
        }
        const agg = aggregationMap.get(name);
        agg.sum += value;
        agg.count += 1;
      });

      const categories = [];
      const averages = [];
      for (const [name, agg] of aggregationMap.entries()) {
        categories.push(name);
        averages.push(agg.count ? agg.sum / agg.count : 0);
      }
      return { categories, averages };
    },
    getTopActiveProvidersItems() {
      if (!this.hasTopActiveProvidersData) return [];

      return this.dashboardData.top10_active_providers.map(item => {
        const totalPriceNum = parseFloat(item.totalPrice);
        const totalProfitNum = parseFloat(item.totalProfit);
        const margin = totalPriceNum > 0 ? (totalProfitNum / totalPriceNum) * 100 : 0;

        return {
          name: item.name,
          consignmentCount: this.formatNumber(item.consignmentCount),
          totalPrice: this.formatCurrency(item.totalPrice),
          totalProfit: this.formatCurrency(item.totalProfit),
          profitMargin: `${this.formatNumber(margin)}%`,
          avgMinDeliveryDay: item.avgMinDeliveryDay,
          avgMaxDeliveryDay: item.avgMaxDeliveryDay
        };
      });
    },

    getProviderNames() {
      if (!this.hasTopActiveProvidersData) return [];
      return this.dashboardData.top10_active_providers.map(item => item.name);
    },

    getConsignmentCountSeries() {
      if (!this.hasTopActiveProvidersData) return [];

      return [
        {
          name: this.t('Consignment Count'),
          data: this.dashboardData.top10_active_providers.map(item => item.consignmentCount)
        }
      ];
    },

    getTotalProfitSeries() {
      if (!this.hasTopActiveProvidersData) return [];

      return [
        {
          name: this.t('Total Profit'),
          data: this.dashboardData.top10_active_providers.map(item =>
            parseFloat(item.totalProfit).toFixed(2)
          )
        }
      ];
    },

    getDeliveryTimeCategories() {
      if (!this.hasProviderStateDeliveryTimesData) return [];
      return this.aggregatedAverageDeliveryByProvider.categories;
    },

    getAverageDeliveryTimeSeries() {
      if (!this.hasProviderStateDeliveryTimesData) return [];

      return [
        {
          name: this.t('Average Delivery Days'),
          data: this.aggregatedAverageDeliveryByProvider.averages
        }
      ];
    },

    stateHeaderLabel() {
      return this.isInternationalShipment ? this.t('State / Region') : this.t('State');
    },

    cityHeaderLabel() {
      return this.isInternationalShipment ? this.t('City / District') : this.t('District');
    },

    isInternationalShipment() {
      const dir = this.shipmentDirection;
      if (dir === null || dir === undefined) return false;
      if (typeof dir === 'string') {
        const s = dir.toLowerCase();
        return (
          s.includes('international') ||
          s.includes('abroad') ||
          s.includes('yurtdışı') ||
          s.includes('yurtdisi') ||
          s.includes('yurt dışı')
        );
      }
      const num = Number(dir);
      return Number.isFinite(num) ? num === 2 : false;
    },

    providerDetailsRows() {
      if (!this.hasProviderStateDeliveryTimesData || !this.selectedProviderName) return [];
      return this.dashboardData.provider_state_delivery_times
        .filter(
          item =>
            (item.provider_name || String(item.provider_id || 'Unknown')) ===
            this.selectedProviderName
        )
        .map(item => ({ state: item.state, city: item.city, avg: item.avg_delivery_day }));
    },

    getConsignmentCountPieData() {
      if (!this.hasTopActiveProvidersData) return [];

      return this.dashboardData.top10_active_providers.map(item => ({
        name: item.name,
        value: item.consignmentCount
      }));
    }
  },
  methods: {
    onDeliveryTimeBarClick(params) {
      // params.name should be the provider label (category)
      const providerName = params && params.name ? params.name : '';
      if (!providerName) return;
      this.selectedProviderName = providerName;
      this.showProviderDetails = true;
    },
    closeProviderDetails() {
      this.showProviderDetails = false;
      this.selectedProviderName = '';
    },
    async loadData() {
      this.loading = true;

      try {
        const args = { currentTab: 3, dateBetween: this.selectedFilter };
        if (this.shipmentDirection !== null && this.shipmentDirection !== undefined) {
          args.transportDirection = this.shipmentDirection;
        }
        const res = await useService.getDashboard(args);
        this.dashboardData = res.data;
      } catch (error) {
        this.dashboardData = {};
      } finally {
        this.loading = false;
      }
    }
  },
  watch: {
    fetchKey: {
      immediate: true,
      handler() {
        if (this.shipmentDirection == null) return;
        this.loadData();
      }
    }
  }
};
</script>

<style scoped>
.pp-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.pp-modal {
  background: #fff;
  width: 90%;
  max-width: 720px;
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  color: #333;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}
.pp-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
}
.pp-modal-body {
  padding: 16px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

/* Dark theme support */
.pp-modal.is-dark {
  background: #283046;
  color: #d0d2d6;
}
.pp-modal.is-dark .pp-modal-header {
  border-bottom-color: #3b4253;
}
.pp-modal.is-dark .table thead th {
  background: #30394c;
  color: #d0d2d6;
  border-color: #3b4253;
}
.pp-modal.is-dark .table tbody td {
  color: #d0d2d6;
  border-color: #3b4253;
}

/* Transitions */
.pp-backdrop-fade-enter-active,
.pp-backdrop-fade-leave-active {
  transition: opacity 0.2s ease;
}
.pp-backdrop-fade-enter,
.pp-backdrop-fade-leave-to {
  opacity: 0;
}

.pp-modal-zoom-enter-active,
.pp-modal-zoom-leave-active {
  transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.25s ease;
}
.pp-modal-zoom-enter {
  transform: scale(0.9);
  opacity: 0;
}
.pp-modal-zoom-leave-to {
  transform: scale(0.95);
  opacity: 0;
}
</style>
